import { firstValueFrom } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { logSessionEvent } from './session-log';

/**
 * Design pick, not a measured value (see design doc "Open Questions").
 * Adjust if real cold-start latency on mobile shows boot feels slow on expiry.
 */
export const BOOTSTRAP_REFRESH_TIMEOUT_MS = 8000;

/**
 * `APP_INITIALIZER` factory: proactively restores the session on app boot
 * when a local access token exists but is expired (`hasToken && !isLoggedIn`).
 *
 * - No local token at all -> resolves immediately, 0 HTTP calls (current
 *   behavior unchanged).
 * - Token still valid -> resolves immediately, 0 HTTP calls.
 * - Token expired, refresh succeeds -> new tokens are stored via the shared,
 *   dedup-safe `AuthService.refreshToken()` (the same call the 401
 *   interceptor uses), boot continues normally on the originally requested
 *   route.
 * - Token expired, refresh fails OR exceeds `BOOTSTRAP_REFRESH_TIMEOUT_MS` ->
 *   full logout (closed product decision: never a silent local-only
 *   clean-up), then boot continues. The in-flight refresh HTTP call is
 *   intentionally NOT aborted on timeout (see `AuthService.refreshToken()`
 *   `refCount:false` + session-epoch guard).
 *
 * The returned promise NEVER rejects: an unhandled rejection here would
 * whitescreen the app's boot sequence.
 */
export function sessionRestoreInitializer(auth: AuthService): () => Promise<void> {
	return () => {
		if (!auth.hasToken || auth.isLoggedIn) {
			return Promise.resolve();
		}

		return firstValueFrom(
			auth.refreshToken().pipe(
				timeout(BOOTSTRAP_REFRESH_TIMEOUT_MS),
				map(() => void 0),
				catchError(() => {
					logSessionEvent({ reason: 'bootstrap.logout.refresh-failed', at: new Date().toISOString() });
					return auth.logout();
				})
			)
		).then(
			() => void 0,
			() => void 0
		);
	};
}
